import React, { useState } from 'react'
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert'
import tripContractService from '../../services/tripContractService'
import { X, Send } from 'lucide-react'
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import Button from '../common/Button.js'
import { useTranslation } from 'react-i18next';


const AddPaymentModal = ({ isOpen, tripId, balance, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    reference_number: '',
    remarks: ''
  })

  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    amount:       [(v) => validators.required(v, 'Payment amount'), (v) => validators.number(v, 0.01)],
    payment_date: (v) => validators.required(v, 'Payment date'),
    payment_mode: (v) => validators.select(v, 'payment mode'),
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    let filtered = value
    if (name === 'amount') filtered = inputFilters.amount(value)
    setFormData({ ...formData, [name]: filtered })

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate(formData, validationRules)) return

    if (parseFloat(formData.amount) > balance) {
      Alert.error('Payment amount cannot exceed balance of ' + formatCurrency(balance))
      return
    }

    try {
      setLoading(true)
      const payload = {
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date,
        payment_mode: formData.payment_mode,
        reference_number: formData.reference_number,
        remarks: formData.remarks
      }

      await tripContractService.addPayment(tripId, payload)
      Alert.success('Payment recorded successfully!')
      onClose()
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to record payment')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">Add Payment</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Balance Info */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-1">Outstanding Balance</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(balance)}</p>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Amount *
            </label>
            <input
              type="text"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="0.00"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                errors.amount ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            <ValidationMessage error={errors.amount} />
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Date *
            </label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                errors.payment_date ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            <ValidationMessage error={errors.payment_date} />
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Mode *
            </label>
            <SelectField variant="form"
              name="payment_mode"
              value={formData.payment_mode}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                errors.payment_mode ? 'border-red-500' : 'border-gray-200'
              }`}
            >
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="other">Other</option>
            </SelectField>
            <ValidationMessage error={errors.payment_mode} />
          </div>

          {/* Reference Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reference Number (Optional)
            </label>
            <input
              type="text"
              name="reference_number"
              value={formData.reference_number}
              onChange={handleInputChange}
              placeholder="e.g., Cheque No, UPI Ref ID"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remarks (Optional)
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              placeholder="Add any additional notes"
              rows="2"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button variant="success" type="submit" loading={loading} icon={Send} className="flex-1">
              {loading ? 'Saving...' : 'Record Payment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddPaymentModal
